import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import Menu from "../Menu";

const PreviewHtmlDocInfo = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [itemData, setItemData] = useState(null);
  const [modifiedHtml, setModifiedHtml] = useState("");
  const [sizePreset, setSizePreset] = useState("phone"); // "phone", "480x800", "800x1232", "custom"
  const [customWidth, setCustomWidth] = useState(800);
  const [customHeight, setCustomHeight] = useState(600);

  // Utility function to get cookie
  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  useEffect(() => {
    const fetchPreviewData = async () => {
      const token = getCookie("token");
      const userid = getCookie("userid");

      if (!token || !userid) {
        console.error("Missing token or userId in localStorage");
        setError("Missing authentication credentials");
        setLoading(false);
        return;
      }

      const requestBody = {
        id: id,
      };

      try {
        const response = await axios.post(
          'https://api.dqr-display-tms.bonrix.in/api/HtmlDocInfoOperation/Search',
          requestBody,
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "application/json-patch+json",
            },
          }
        );

        if (response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          const item = response.data.data[0];
          setItemData(item);
          
          // Fetch and modify HTML file
          if (item.filepath) {
            try {
              const htmlResponse = await fetch(item.filepath);
              const htmlContent = await htmlResponse.text();
              
              // Modify the HTML: swap comments on jsondata lines
              const modifiedContent = modifyHtmlContent(htmlContent);
              setModifiedHtml(modifiedContent);
            } catch (htmlError) {
              console.error("Error fetching HTML file:", htmlError);
              setError("Failed to load HTML file");
            }
          }
        } else {
          setItemData(null);
          setError("Item not found");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load preview");
        setItemData(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPreviewData();
    }
  }, [id]);

  // Function to modify HTML content: uncomment first jsondata line and comment second
  const modifyHtmlContent = (htmlContent) => {
    // Pattern to match:
    // First line: // const jsondata = `{"orderid": "ord1234","bankrrn": "789379793","date": "22-10-2025","amount": "100"}`;
    // Second line: const jsondata = `<jsonvalue>`;
    
    let modified = htmlContent;
    
    // Match commented first line (with JSON object data)
    // Pattern: // const jsondata = `{...}`;
    const firstLinePattern = /\/\/\s*const\s+jsondata\s*=\s*`\{[^`]+\}`;/g;
    
    // Match uncommented second line (with <jsonvalue>)
    // Pattern: const jsondata = `<jsonvalue>`;
    const secondLinePattern = /const\s+jsondata\s*=\s*`<jsonvalue>`;/g;
    
    // Uncomment the first line (remove // from the line with JSON object)
    modified = modified.replace(firstLinePattern, (match) => {
      // Remove the // comment and any leading whitespace after it
      return match.replace(/\/\/\s*/, '');
    });
    
    // Comment the second line (add // to the line with <jsonvalue>)
    modified = modified.replace(secondLinePattern, (match) => {
      // Add // comment before the line
      return '// ' + match;
    });
    
    return modified;
  };

  // Function to download the modified HTML file
  const handleDownload = () => {
    if (!modifiedHtml || !itemData) {
      alert("HTML content not available for download");
      return;
    }

    // Create a blob with the modified HTML content
    const blob = new Blob([modifiedHtml], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Use the original filename or create a default one
    const filename = itemData.filename || `preview_${itemData.id}_${itemData.name}.html`;
    link.download = filename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <>
        <Menu />
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>Loading preview...</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Menu />
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p style={{ color: "red" }}>{error}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="d-flex justify-content-between align-items-center w-100">
                  <h1 className="mb-0">
                    Preview - {itemData?.name || "HTML Document Info"}
                  </h1>
                </div>
              </div>
            </div>
          </section>
          <section className="content">
            <div className="container-fluid">
              {itemData && (
                <div className="card">
                  <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h3 className="card-title mb-1">ID: {itemData.id} - {itemData.name}</h3>
                        {itemData.remark && (
                          <p className="text-muted mb-0">Description: {itemData.remark}</p>
                        )}
                      </div>
                      <div className="d-flex align-items-center">
                        <button
                          type="button"
                          className="btn btn-success btn-sm mr-3"
                          onClick={handleDownload}
                          disabled={!modifiedHtml}
                          title="Download modified HTML file"
                        >
                          <i className="fas fa-download mr-1"></i> Download HTML
                        </button>
                        <label className="mr-2 mb-0">Display Size:</label>
                        <select
                          className="form-control form-control-sm mr-2"
                          style={{ width: "150px" }}
                          value={sizePreset}
                          onChange={(e) => setSizePreset(e.target.value)}
                        >
                          <option value="phone">Phone Size (375 x 667)</option>
                          <option value="480x800">480 x 800</option>
                          <option value="800x1232">800 x 1232</option>
                          <option value="custom">Custom</option>
                        </select>
                        {sizePreset === "custom" && (
                          <>
                            <input
                              type="number"
                              className="form-control form-control-sm mr-2"
                              style={{ width: "100px" }}
                              placeholder="Width"
                              value={customWidth}
                              onChange={(e) => setCustomWidth(parseInt(e.target.value) || 800)}
                            />
                            <span className="mr-2">x</span>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ width: "100px" }}
                              placeholder="Height"
                              value={customHeight}
                              onChange={(e) => setCustomHeight(parseInt(e.target.value) || 600)}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="card-body p-3">
                    <div 
                      style={{ 
                        width: "100%", 
                        height: "80vh", 
                        overflow: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        backgroundColor: "#f5f5f5",
                        padding: "20px"
                      }}
                    >
                      <div
                        style={{
                          width: sizePreset === "phone" ? "375px" : sizePreset === "480x800" ? "480px" : sizePreset === "800x1232" ? "800px" : `${customWidth}px`,
                          height: sizePreset === "phone" ? "667px" : sizePreset === "480x800" ? "800px" : sizePreset === "800x1232" ? "1232px" : `${customHeight}px`,
                          border: "2px solid #333",
                          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                          backgroundColor: "#fff",
                          overflow: "auto"
                        }}
                      >
                        {modifiedHtml ? (
                          <iframe
                            title="HTML Preview"
                            srcDoc={modifiedHtml}
                            style={{
                              width: "100%",
                              height: "100%",
                              border: "none",
                            }}
                            sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                          />
                        ) : (
                          <div style={{ padding: "20px", textAlign: "center" }}>
                            <p>Loading HTML content...</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default PreviewHtmlDocInfo;

